import React, { JSX } from 'react';
import { Box, Container, Text, Heading, Flex, VStack, HStack, Icon, Grid } from '@chakra-ui/react';
import CtaButton from 'components/CtaButton';
import { Star, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import HeroVisual, { DEFAULT_HERO_VISUAL_ITEMS } from 'components/heroVisual';

const MotionBox = motion.create(Box);

const MicroStat = ({ top, bottom }: { top: string; bottom: string }) => (
	<HStack align="baseline" gap={1.5}>
		<Text fontSize="15px" fontWeight="700" letterSpacing="tight" color="black">
			{top}
		</Text>
		<Text fontSize="12px" color="gray.500" fontWeight="500">
			{bottom}
		</Text>
	</HStack>
);

interface PropsInterface {
	title: string;
	copy: string;
	subtitle?: string | JSX.Element;
	ctaPrimary?: {
		text: string;
		href: string;
	};
	content?: React.ReactNode;
}

const HeroSection = (props: PropsInterface) => {
	const highlightText = (text: string | JSX.Element, highlightStyle?: any) => {
		if (typeof text !== 'string') return text;

		return text.split(/(\*\*.*?\*\*|\n)/g).map((part, index) => {
			if (part.startsWith('**') && part.endsWith('**')) {
				return (
					<Text as="span" key={index} {...highlightStyle} color={'gray.500'} fontWeight={400}>
						{part.slice(2, -2)}
					</Text>
				);
			}
			if (part === '\n') {
				return <br key={index} />;
			}
			return <span key={index}>{part}</span>;
		});
	};

	return (
		<Box
			as="section"
			position="relative"
			overflowX="clip"
			bg="white"
			pt={{ base: '100px', lg: '120px' }}
			pb={{ base: '80px', lg: '120px' }}
		>
			{/* Ambient dots background */}
			<Box
				position="absolute"
				inset={0}
				pointerEvents="none"
				opacity={0.05}
				style={{
					backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
					backgroundSize: '28px 28px',
				}}
			/>

			<Container maxW="1400px" px={8} position="relative" zIndex={10}>
				<Grid templateColumns={{ base: '1fr', lg: '1.05fr 1fr' }} gap={12} alignItems="center">
					{/* Left Content */}
					<VStack align={{ base: 'center', lg: 'start' }} gap={0}>
						<MotionBox
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
						>
							<HStack
								bg="realWhite"
								w={'fit-content'}
								border="1px solid"
								borderColor="gray.100"
								px={3}
								py={1}
								borderRadius="full"
								gap={2}
								mb={5}
							>
								<Icon as={Sparkles} boxSize={3} color="primary" />
								<Text fontSize="11px" fontWeight="500" color="gray.500" letterSpacing="tight">
									{props.copy}
								</Text>
							</HStack>

							<Heading
								as="h2"
								fontSize={{ base: '44px', md: '72px', lg: '80px' }}
								fontWeight={500}
								lineHeight={1}
								letterSpacing="-0.04em"
								color="black"
								textAlign={{ base: 'center', lg: 'left' }}
								mb={5}
							>
								{highlightText(props.title)}
							</Heading>

							<Heading
								as="h1"
								fontSize="17px"
								lineHeight="1.5"
								color="gray.500"
								maxW="xl"
								textAlign={{ base: 'center', lg: 'left' }}
								mb={6}
								fontWeight="500"
							>
								{props.subtitle}
							</Heading>

							<Flex direction={{ base: 'column', sm: 'row' }} align="center" gap={3} mb={8}>
								{props.ctaPrimary && (
									<CtaButton href={props.ctaPrimary.href} hoverScale shadow="2xl">
										{props.ctaPrimary.text}
										<Icon as={ArrowRight} ml={1} boxSize={4} />
									</CtaButton>
								)}
								{/*<Text fontSize="13.5px" fontWeight="500" color="gray.600" py={3} px={1}>*/}
								{/*	No credit card required*/}
								{/*</Text>*/}
							</Flex>

							{/* Trust strip */}
							<Flex
								wrap="wrap"
								align="center"
								gapX={8}
								gapY={4}
								justify={{ base: 'center', lg: 'start' }}
							>
								<HStack gap={2}>
									<HStack gap={0.5}>
										{[...Array(5)].map((_, i) => (
											<Icon
												key={i}
												as={Star}
												boxSize={3.5}
												color="orange.400"
												fill="currentColor"
											/>
										))}
									</HStack>
									<Text fontSize="13px" color="gray.500" fontWeight="500">
										<Text as="span" fontWeight="700" color="black">
											3,000+
										</Text>{' '}
										users
									</Text>
								</HStack>
								{/*<MicroStat top="4.2×" bottom="Avg. Speed" />*/}
								<MicroStat top="4.9/5" bottom="Rating" />
								<MicroStat top="10k+" bottom="Generations" />
							</Flex>
						</MotionBox>
					</VStack>

					{/* Right Content — custom visual or default HeroVisual */}
					{props.content ?? <HeroVisual items={DEFAULT_HERO_VISUAL_ITEMS} />}
				</Grid>
			</Container>
		</Box>
	);
};

export default HeroSection;
